import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { PlatformList, PlatformService } from '@application/platform.service';
import { Button, CellulaFormControl, CellulaFormSubmitData, FileViewer, Flex, Form, InputTextArea, Spacer } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AttachedFile } from '@feature/file_manager/attached_file.component';
import { FileManagerService } from '@feature/file_manager/file_manager.service';
import { useFileWorkerList } from '@feature/file_manager/use_file_worker_list';
import { quirk_iOSKeyboardLockBodyScroll } from '@module/quirk_ios_keyboard_lock_body_scroll';
import { useService } from '@visiba/instantiation';
import { rem } from '@visiba/std';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface Props {
	onSend: (message: string | null, fileTickets: string[]) => void;
	fileUploadDispositionUri: string;
	isReadyForCommunication: boolean;
	hideFileUpload?: boolean;
	/**
	 * This is passed down to make sure that a11y focus when chat is open/changed gets applied correctly.
	 */
	connectedId: string;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		min-height: 130px;
		width: 100%;
		padding: ${tokens.spacing.x1} ${tokens.spacing.x2} ${tokens.spacing.x2_5};
		container: ChatInputAreaContainer / inline-size;

		@media screen and (min-width: 905px) {
			padding: ${tokens.spacing.x1} ${tokens.spacing.x4} ${tokens.spacing.x2_5};
		}
	`,
	senderContainer: styled.div`
		display: flex;
		position: relative;
		flex-direction: column;
		padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
		border-radius: 8px;
		width: 100%;
		gap: ${tokens.spacing.x0_5};
	`,
	attachedFilesContainer: styled.div`
		margin-top: ${tokens.spacing.x1_5};
		display: flex;
		flex-wrap: wrap;
		gap: ${tokens.spacing.x1_5};

		@container ChatInputAreaContainer (min-width: 950px) {
			margin-top: ${tokens.spacing.x1_5};
			display: grid;
			grid-template-columns: repeat(3, 1fr);
			gap: ${tokens.spacing.x1};
		}
	`,
	inputContainer: styled.div`
		position: relative;
	`,
	input: styled.input`
		all: unset;
		color: ${tokens.color.content.default};

		&::placeholder {
			color: ${tokens.color.content.placeholder};
		}
	`,
	validationErrorMessage: styled.span`
		color: ${tokens.color.danger.contentMuted};
		position: absolute;
		top: 100%;
		left: 0;
		padding: 0 5px;
	`,
	attachedFileMotion: styled(motion.div)`
		width: 100%;
		transform-origin: bottom left 60px;
		min-width: 0;

		${tokens.breakpointQuery.medium} {
			width: unset;
		}
	`,
	icon: styled.span`
		vertical-align: middle;
		font-size: ${rem(21)};
		color: ${tokens.color.danger.border};
		position: absolute;
		top: auto;
		right: ${tokens.spacing.x1_5};
	`,
};

const MAX_CHARACTERS = 4000;

export function ChatInputArea({
	onSend,
	fileUploadDispositionUri,
	isReadyForCommunication,
	connectedId,
	hideFileUpload = false,
}: Props) {
	const fileManagerService = useService(FileManagerService);
	const fileWorkers = useFileWorkerList(fileManagerService);
	const [disableSend, setDisableSend] = useState(false);
	const hiddenFileInput = useRef<HTMLInputElement>(null);
	const analyticCtx = useAnalyticContext();
	const platformService = useService(PlatformService);
	const containerRef = useRef(null);
	const [showCharCount, setShowCharCount] = useState(false);

	const inputRef = useRef<HTMLInputElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(
		function whenChatIsOpenSetFocusOnRegion() {
			if (platformService.runtimeSupportsTouch === false) {
				inputRef.current?.focus();
			}
		},
		[connectedId, platformService],
	);

	const uploadFiles = useCallback(
		function uploadFiles(fileList: Nullable<FileList>) {
			if (fileList != null) {
				fileManagerService.assignWorker(fileList, fileUploadDispositionUri);
			}

			// This needs to check for another batch of files after the first batch is
			// being uploaded.
			setDisableSend(true);
			queueMicrotask(async () => {
				const workers = fileManagerService.peakWorkerList();
				const promises = [];
				for (const worker of workers) {
					promises.push(worker.upload());
					analyticCtx.trackEvent('Uploaded attachment');
				}

				await Promise.allSettled(promises)
					.then(() => {
						setDisableSend(false);
					})
					.catch(() => {
						setDisableSend(true);
					});
			});
		},
		[analyticCtx, fileManagerService, fileUploadDispositionUri],
	);

	useEffect(
		function bindPasteImage() {
			const abortController = new AbortController();

			document.addEventListener(
				'paste',
				(e) => {
					const clipboardItems = e.clipboardData?.files ?? new FileList();

					if (clipboardItems.length > 0) {
						e.preventDefault();
						uploadFiles(clipboardItems);
					}
				},
				{
					signal: abortController.signal,
				},
			);

			return () => {
				abortController.abort();
			};
		},
		[uploadFiles],
	);

	function handleUploadFileButtonClick() {
		hiddenFileInput.current?.click();
	}

	function handleSubmit(body: CellulaFormSubmitData) {
		const expectedUploadsLength = fileManagerService.peakUploadedWorkers().length;
		const addedWorkersLength = fileManagerService.peakWorkerList().length;

		if (!isReadyForCommunication) return false;
		if (addedWorkersLength !== 0 && expectedUploadsLength !== addedWorkersLength) {
			return false;
		}

		const fileTickets = fileManagerService.flushUploadedIdentities();
		onSend(body.message ? body.message : null, fileTickets);

		return true;
	}

	function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
		uploadFiles(event.target.files);

		event.target.value = '';
		inputRef.current?.focus();
		// TODO PAT-569: Send message to aria-live
	}

	function handleRemoveFileClick(id: string) {
		fileManagerService.dropWorker(id);
		analyticCtx.trackEvent('Removed uploaded attachment');
	}

	function handleInputMessageBlur() {
		quirk_iOSKeyboardLockBodyScroll();
	}

	function handleEnterKey(event: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (event.key !== 'Enter') return;
		if (platformService.platformIs(PlatformList.Mobile)) return;
		if (event.shiftKey) return;

		event.preventDefault();
		event.currentTarget.form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
	}

	function handleAfterSubmit(_: CellulaFormSubmitData, control: CellulaFormControl) {
		control.reset({
			message: '',
		});
	}

	return (
		<Form.Root
			onSubmit={handleSubmit}
			onAfterSubmit={handleAfterSubmit}
			options={{
				defaultValues: {
					message: '',
				},
			}}
		>
			<elements.container ref={containerRef}>
				<elements.senderContainer>
					<InputTextArea
						onBlur={handleInputMessageBlur}
						onKeyDown={handleEnterKey}
						name='message'
						label=''
						placeholder={withTranslation('C.Component.AppointmentChat.MessageInput.Placeholder')}
						maxHeight={200}
						autoHeight
						// TODO - CHAR COUNT REACT HOOK FORM: These character count props should be replace with react hook form later.
						characterCount={MAX_CHARACTERS}
						alwaysShowCharacterCount={showCharCount}
						onChange={(e) => {
							setShowCharCount(e.target.value.length > MAX_CHARACTERS - 500);
						}}
						options={{
							maxLength: {
								message: withTranslation('PW.Component.Chat.InputValidationErrorMessage.MaxCharNumber'),
								value: MAX_CHARACTERS,
							},
						}}
					/>

					{fileWorkers.length > 0 ? (
						<FileViewer.Root showDownload={false}>
							<elements.attachedFilesContainer>
								<AnimatePresence presenceAffectsLayout>
									{fileWorkers.map((worker, index) => (
										<elements.attachedFileMotion
											key={worker.id}
											custom={index}
											layout
											variants={motionVariants}
											initial='hidden'
											animate='visible'
											exit='hidden'
										>
											<AttachedFile worker={worker} onRemoveClick={handleRemoveFileClick} />
										</elements.attachedFileMotion>
									))}
								</AnimatePresence>
							</elements.attachedFilesContainer>
						</FileViewer.Root>
					) : null}
				</elements.senderContainer>

				<Spacer vertical={tokens.spacing.x2} />

				<Flex direction='row' align='center' style={{ width: '100%' }}>
					{!hideFileUpload && (
						<>
							<Button
								disabled={!isReadyForCommunication}
								disabledText=''
								variant='ghost'
								size='medium'
								icon='paperClip'
								iconPosition='leading'
								onClick={handleUploadFileButtonClick}
							>
								{withTranslation('PW.Component.Chat.Button.File')}
							</Button>
							<input type='file' ref={hiddenFileInput} onChange={handleFileChange} multiple hidden tabIndex={0} />
						</>
					)}
					<div style={{ flex: 1 }} />
					<Form.SubmitButton
						variant='ghost'
						size='medium'
						icon='paperPlaneFilled'
						iconPosition='trailing'
						disabled={disableSend || !isReadyForCommunication}
						disabledText=''
					>
						{withTranslation('PW.Component.Chat.Button.Send')}
					</Form.SubmitButton>
				</Flex>
			</elements.container>
		</Form.Root>
	);
}

const motionVariants: Variants = {
	hidden: {
		opacity: 0,
		scale: 0.9,
		transition: {
			stiffness: 500,
			damping: 40,
			type: 'spring',
		},
	},
	visible: (custom: number) => ({
		scale: 1,
		opacity: 1,
		transition: {
			delay: custom * 0.025,
			stiffness: 500,
			damping: 40,
			type: 'spring',
		},
	}),
};
